import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Switch } from '@tempmee/react';
import {
  fetchNotificationSettings,
  updateUserNotificationSettings,
} from '../../actions/userActions';
import { createErrorSelector, createLoadingSelector } from '../../apis/selectors';
import { useNotificationTypeMap } from '../../constants';
import warningAlert from '../../images/warning_alert.svg';
import { Spinner } from './Spinner';
import { TabViewContainer } from './TabViewContainer';

// Read from the store's per-request slices rather than from whether settings
// are present: the settings outlive the account they were loaded for, so their
// presence says nothing about whether they are this account's.
const FETCH_SETTINGS_REQUEST = ['FETCH_USER_NOTIFICATION_SETTINGS'];
const isFetchingSettingsSelector = createLoadingSelector(FETCH_SETTINGS_REQUEST);
const hasSettingsFetchFailedSelector = createErrorSelector(FETCH_SETTINGS_REQUEST);

export default function NotificationSettings({ userId = null }) {
  const dispatch = useDispatch();
  const notificationTypeMap = useNotificationTypeMap();

  const shouldShowWarning = (type, settings) =>
    notificationTypeMap[type]?.isAlertAvailable &&
    (notificationTypeMap[type]?.isPush ? !settings?.push : true) &&
    (notificationTypeMap[type]?.isEmail ? !settings?.email : true) &&
    (notificationTypeMap[type]?.isText ? !settings?.text : true);

  // Null until settings first arrive, so nothing renders before the fetch has
  // started either.
  const [currentSettings, setCurrentSettings] = useState(null);

  const notificationSettings = useSelector((state) => state.users.notificationSettings);
  const isFetchingSettings = useSelector(isFetchingSettingsSelector);
  const hasSettingsFetchFailed = useSelector(hasSettingsFetchFailedSelector);

  // What the store held at mount belongs to the last account opened, not to
  // `userId`. Copying it would paint that account's switches for one frame when
  // this fetch succeeds, since loading ends in the same dispatch.
  const settingsAtMount = useRef(notificationSettings);

  useEffect(() => {
    dispatch(fetchNotificationSettings(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (notificationSettings && notificationSettings !== settingsAtMount.current) {
      setCurrentSettings(reorderNotifications(notificationSettings, notificationTypeMap));
    }
  }, [notificationSettings]);

  const renderSwitch = (code, checked, eventType) => {
    const titleMap = {
      push: 'Push',
      email: 'Email',
      text: 'SMS/Text',
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', columnGap: 20 }}>
        <Switch
          checked={checked}
          onChange={() => {
            const newSettings = currentSettings.map((notification) => {
              if (notification.event === eventType) {
                return {
                  ...notification,
                  [code]: !checked,
                };
              }

              return notification;
            });

            setCurrentSettings(newSettings);
          }}
        />

        <h5 className="bold color--dark-100" style={{ margin: 0 }}>
          {titleMap[code]}
        </h5>
      </div>
    );
  };

  const renderNotificationSection = (type, settings) => {
    const showWarning = shouldShowWarning(type, settings);

    if (!notificationTypeMap[type]) {
      return <></>;
    }

    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          borderBottom: '1px solid #EDEDED',
          padding: '20px 0',
        }}
      >
        <div style={{ width: '50%', marginLeft: 20 }}>
          <div style={{ display: 'flex' }}>
            <h5
              className="bold color--dark-100"
              style={{
                fontSize: 16,
                marginBottom: 10,
                marginRight: 10,
              }}
            >
              {notificationTypeMap?.[type]?.title}
            </h5>

            {showWarning && (
              <img
                src={warningAlert}
                style={{ width: 18, objectFit: 'contain' }}
                alt="warning_alert"
              />
            )}
          </div>

          <p className="color--dark-200">{notificationTypeMap?.[type]?.description}</p>
        </div>

        <div
          style={{
            marginLeft: 120,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            rowGap: 18,
          }}
        >
          {settings?.push !== undefined &&
            notificationTypeMap?.[type]?.isPush &&
            renderSwitch('push', settings?.push, type)}
          {settings?.email !== undefined &&
            notificationTypeMap?.[type]?.isEmail &&
            renderSwitch('email', settings?.email, type)}
          {settings?.text !== undefined &&
            notificationTypeMap?.[type]?.isText &&
            renderSwitch('text', settings?.text, type)}
        </div>
      </div>
    );
  };

  // `state.users.notificationSettings` is shared by every account opened in the
  // session and still holds the last one's until this fetch succeeds, so the
  // switches and Save stay hidden while it is pending and after it fails —
  // otherwise Save would write that account's settings onto `userId`.
  const renderSettings = () => {
    if (hasSettingsFetchFailed) {
      return (
        <Alert
          severity="error"
          title="Unable to load notification settings"
          description="The notification service may be unavailable. Please try again later."
          action={
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => dispatch(fetchNotificationSettings(userId))}
            >
              Retry
            </Button>
          }
        />
      );
    }

    if (isFetchingSettings || !currentSettings) {
      return (
        <Box py={6}>
          <Spinner />
        </Box>
      );
    }

    return (
      <>
        {currentSettings.map(({ event, ...settings }) => (
          <Fragment key={event}>{renderNotificationSection(event, settings)}</Fragment>
        ))}

        <Box pt={20} pb={12}>
          <Button
            onClick={() => dispatch(updateUserNotificationSettings(currentSettings, userId))}
          >
            Save
          </Button>
        </Box>
      </>
    );
  };

  return (
    <TabViewContainer
      style={{
        alignItems: 'center',
        padding: '0 90px',
        width: '80%',
      }}
      title="Notifications Settings"
      variant="card"
    >
      <div style={{ width: '100%', borderBottom: '1px solid #EDEDED' }}>
        <h5 className="bold" style={{ fontSize: 16, color: '#3EB0CA', marginLeft: 20 }}>
          Notification Type
        </h5>
      </div>

      {renderSettings()}
    </TabViewContainer>
  );
}

const reorderNotifications = (notifications, notificationTypeMap) => {
  const orderMap = {
    OFFICE_EVENT_SINGLE_SHIFT_JOB_POSTED: 1,
    OFFICE_EVENT_MULTI_DAY_JOB_POSTED: 2,
    OFFICE_EVENT_OFFER_ACTION_REQUIRED: 3,
    OFFICE_EVENT_OFFER_RECEIVED: 4,
    OFFICE_EVENT_COUNTER_OFFER_RECEIVED: 5,
    OFFICE_EVENT_COUNTER_OFFER_EXPIRATION_WARNING_1_HOUR: 6,
    OFFICE_EVENT_COUNTER_OFFER_EXPIRATION_WARNING_15_MINUTES: 7,
    OFFICE_EVENT_OFFER_EXPIRATION: 8,
    OFFICE_EVENT_MESSAGE_RECEIVED: 9,
    OFFICE_EVENT_JOB_CANCELLED_BY_PROFESSIONAL: 10,
    OFFICE_EVENT_CANCELLED_JOB_REFILLED: 11,
  };

  notifications.sort((a, b) => {
    const indexA = orderMap[a.event];
    const indexB = orderMap[b.event];

    if (indexA === undefined && indexB === undefined) {
      return 0;
    }
    if (indexA === undefined) {
      return 1;
    }
    if (indexB === undefined) {
      return -1;
    }
    return indexA - indexB;
  });

  return notifications.filter((notification) => !notificationTypeMap[notification.event]?.hidden);
};
